import { useState, useEffect, useRef, useCallback } from "react";
import { GeneralAPI } from "../../utils/generalApi";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import debounce from "lodash.debounce";
import { maxAutocompletePageSize } from "../../constants/api";

function completeCreationForm({
  CreationForm,
  open,
  setOpen,
  initialValue,
  setValue,
  multiple,
}) {
  return (props) =>
    open ? (
      <CreationForm
        {...props}
        open={open}
        setOpen={setOpen}
        initialValue={initialValue}
        setValue={setValue}
        multiple={multiple}
      />
    ) : (
      <></>
    );
}

export default function ModelAutoComplete({
  modelName,
  label,
  value,
  setValue,
  defaultParams,
  allowCreation,
  CreationForm,
  maxSize,
  includeFields = [],
  TextFieldProps,
  ...props
}) {
  const [open, setOpen] = useState(false);
  const [creationFormOpen, setCreationFormOpen] = useState(false);
  const [creationFormStartValue, setCreationFormStartValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const generalAPIRef = useRef(
    new GeneralAPI(modelName, {
      customSerializer: true,
      values: ["uuid", ...includeFields],
      size: maxSize ?? maxAutocompletePageSize,
    })
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchOptions = useCallback(
    debounce(async (input) => {
      let searchInput = typeof input == "object" ? "" : input;
      generalAPIRef.current.update({
        searchInput: searchInput,
        filter: defaultParams || {},
      });
      const { data } = await generalAPIRef.current.send();
      const formattedData = data.data.map((item) => ({
        label: item.__str__ || "",
        uuid: item.uuid,
        data:
          includeFields.length > 0
            ? (() => {
                return Object.fromEntries(
                  Object.entries(item).filter(
                    ([key]) => !["uuid", "__str__"].includes(key)
                  )
                );
              })()
            : null,
      }));
      let finalOptions = formattedData;
      if (value) {
        const currentValuesMap = Array.isArray(value)
          ? Object.fromEntries(value.map((val) => [val.uuid, val]))
          : Object.fromEntries([[value.uuid, value]]);

        finalOptions = finalOptions.filter(
          (option) => !currentValuesMap[option.uuid]
        );
        finalOptions = [...Object.values(currentValuesMap), ...finalOptions];
      }
      setOptions(finalOptions);
      setLoading(false);
    }, 300),
    [defaultParams, modelName]
  );

  useEffect(() => {
    if (open) {
      setLoading(true);
      fetchOptions(inputValue);
    } else {
      setLoading(false);
      setOptions([]);
    }
  }, [open, fetchOptions, inputValue]);

  useEffect(() => {
    if (value == null && !open) {
      setInputValue("");
    }
  }, [value, open]);

  const CreationFormComplete = CreationForm
    ? completeCreationForm({
        CreationForm,
        open: creationFormOpen,
        setOpen: setCreationFormOpen,
        initialValue: creationFormStartValue,
        setValue: setValue,
        multiple: props.multiple ?? false,
      })
    : () => <></>;

  return (
    <>
      <Autocomplete
        value={value}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
        noOptionsText="No options"
        getOptionKey={(option) => option.uuid}
        isOptionEqualToValue={(option, value) => {
          if (typeof value == "string") {
            return option.uuid === value;
          }
          return option.uuid === value.uuid;
        }}
        getOptionLabel={(option) => {
          if (typeof option == "string") {
            return option;
          }
          return option.label;
        }}
        options={options}
        loading={loading}
        clearOnBlur
        filterOptions={(options, params) => {
          let matchingTitle = false;
          options = options.filter((option) => {
            if (
              option.label.toLowerCase() === params.inputValue.toLowerCase() &&
              !matchingTitle
            ) {
              matchingTitle = true;
            }

            return option.label
              .toLowerCase()
              .includes(params.inputValue.toLowerCase());
          });

          if (!allowCreation) {
            return options;
          }

          if (params.inputValue.trim() !== "" && !matchingTitle) {
            options.push({
              label: `Add "${params.inputValue}"`,
              inputValue: params.inputValue,
            });
          }

          return options;
        }}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={(event, newValue) => {
          // Handle multiple selections (array case)
          if (Array.isArray(newValue)) {
            // Look for an option that has an inputValue (the creation option)
            const creationOption = newValue.find(
              (option) => option && option.inputValue
            );
            if (creationOption) {
              // Remove the creation option from the array if needed
              const filteredValues = newValue.filter(
                (option) => !option.inputValue
              );
              setValue(filteredValues);
              // Trigger creation form with the input value from the creation option
              setCreationFormStartValue(creationOption.inputValue);
              setCreationFormOpen(true);
              // Clear the input value
              setInputValue("");
              return;
            }
            // If no creation option, simply update the value normally
            setValue(newValue);
          } else {
            // Single selection case (if applicable)
            if (typeof newValue === "string") {
              setCreationFormStartValue(newValue);
              setCreationFormOpen(true);
              setInputValue("");
              setValue(null);
            } else if (newValue && newValue.inputValue) {
              setCreationFormStartValue(newValue.inputValue);
              setCreationFormOpen(true);
              setValue(null);
              setInputValue("");
            } else {
              setValue(newValue);
            }
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            {...TextFieldProps}
            variant={props?.variant ? props.variant : "standard"}
            label={label}
            required={props?.required}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                variant={"standard"}
                size="small"
                key={key}
                label={option.label}
                {...tagProps}
              />
            );
          })
        }
        {...props}
      />
      <CreationFormComplete />
    </>
  );
}
